import { createClient } from "contentful"
import _ from "lodash"
import { Moment } from "moment"
import { richTextFromMarkdown, FallbackResolver } from "@contentful/rich-text-from-markdown"
import { Node } from "@contentful/rich-text-types"
import fetch from "node-fetch"
import { unfurl } from "unfurl.js"
import { UnPromisify } from "@superwall-me/schema"

type ContentfulOptions = {
  usePreview?: boolean
}

export const fixContentfulUrl = (url: string | undefined) => {
  return url !== undefined ? (url.startsWith("//") ? `https:${url}` : url) : ""
}

export class Contentful {
  private client: ReturnType<typeof createClient>
  private readonly fields = [
    "fields.title",
    "fields.subtitle",
    "fields.slug",
    "fields.author",
    "fields.date",
    "fields.updatedPostPublishDate",
    "fields.image",
    "metadata.tags",
    "sys.createdAt",
    "sys.updatedAt",
  ] as const

  constructor(options: ContentfulOptions = {}) {
    const productionAccessToken =
      process.env.CONTENTFUL_ACCESS_TOKEN || "CYrz6I1NPa8oh5CA4dFDFFvZB0KkOIUt6ysECrrm2Nk"
    const previewAccessToken =
      process.env.CONTENTFUL_ACCESS_TOKEN_PREVIEW || "CYrz6I1NPa8oh5CA4dFDFFvZB0KkOIUt6ysECrrm2Nk"

    this.client = createClient({
      host: options.usePreview ? "preview.contentful.com" : "cdn.contentful.com",
      space: process.env.CONTENTFUL_SPACE_ID || "3k7cygmwfm7x",
      accessToken: options.usePreview ? previewAccessToken : productionAccessToken,
    })
  }

  async getMostRecentPost(): Promise<Post | undefined> {
    const posts = await this.client.getEntries({
      content_type: "post",
      select: [...this.fields] as any,
      "fields.hidden[ne]": true,
      order: "-sys.createdAt" as any,
      limit: 1,
    })

    return posts.items.length > 0 ? await this.mapPost(posts.items[0]) : undefined
  }

  async getAllPosts(limit?: number) {
    const posts = await this.client.getEntries({
      content_type: "post",
      select: [...this.fields] as any,
      "fields.hidden[ne]": true,
      order: ["-sys.createdAt"] as any,
      ...(limit ? { limit } : {}),
    })

    return await Promise.all(posts.items.map((post) => this.mapPost(post)))
  }

  async getPostsByTag(tag: string): Promise<Post[]> {
    const posts = await this.client.getEntries({
      content_type: "post",
      // @ts-expect-error
      "metadata.tags.sys.id": tag,
      "fields.hidden[ne]": true,
      select: this.fields,
      order: "-sys.createdAt",
    })

    // map posts async
    return await Promise.all(posts.items.map((post) => this.mapPost(post)))
  }

  async getPostSlugs(limit?: number): Promise<string[]> {
    const posts = await this.client.getEntries({
      content_type: "post",
      select: ["fields.slug"],
      order: "-sys.createdAt" as any,
      ...(limit ? { limit } : {}),
    })

    return posts.items.map((post) => post.fields.slug as string)
  }

  async getPageSlugs(): Promise<string[]> {
    const posts = await this.client.getEntries({
      content_type: "page",
      select: ["fields.slug"],
      order: "-sys.createdAt" as any,
    })

    return posts.items.map((post) => post.fields.slug as string)
  }

  async getPostBySlug(slug: string): Promise<Post | undefined> {
    const post = await this.client.getEntries({
      content_type: "post" as any,
      "fields.slug": slug,
      select: [...this.fields, "fields.body", "fields.markdownBody"],
      order: ["-sys.createdAt"] as any,
    })

    if (post.items.length === 0) {
      return undefined
    }

    const postItem = post.items[0]

    return await this.mapPost(postItem)
  }

  async getPageBySlug(slug: string): Promise<Page | undefined> {
    const post = await this.client.getEntries({
      content_type: "page" as any,
      "fields.slug": slug,
      order: "-sys.createdAt" as any,
    })

    if (post.items.length === 0) {
      return undefined
    }

    const item = post.items[0]

    return this.mapPage(item)
  }

  async mapPost(post: any): Promise<Post> {
    const image = post.fields.image || false

    let body = post.fields.body || undefined

    const fallback: FallbackResolver = async (input) => {
      let node = input as any

      if (!input) {
        return []
      }

      if (node.type == "code") {
        return {
          nodeType: "paragraph",
          data: {
            code: node.value,
            language: node.lang,
          },
          content: [],
        } as Node

        // return {
        //   nodeType: "paragraph",
        //   value: node.value,
        //   marks: [
        //     {
        //       type: "code",
        //     },
        //   ],
        //   data: {
        //     code: node.value,
        //     language: node.lang,
        //   },
        //   node,
        // } as Node
      }

      if (true) {
        // if the node doesn't have https add it

        let url = fixContentfulUrl(node.url)

        // fetch the content type without downloading the file
        // console.log("fetching", url)
        let res: UnPromisify<ReturnType<typeof fetch>>
        try {
          res = await fetch(url, { method: "HEAD" })
        } catch (e) {
          console.error("Failed to fetch", url, e)
          return []
        }
        // const res = await fetch(url, { method: "HEAD" })

        let contentType = res.headers.get("content-type") || "text/html"

        // if contentType is html, request the full html
        if (contentType.includes("text/html")) {
          const result = await unfurl(url).catch((e) => {
            console.error("Failed to unfurl", url, e)
            return null
          })
          let title = node.alt || result?.title || result?.open_graph.title || ""
          let description = node.title || result?.open_graph.description || ""
          let image =
            result?.open_graph && result.open_graph.images && result.open_graph.images.length > 0
              ? result.open_graph.images[0]?.url || ""
              : ""
          let siteName = result?.open_graph.site_name || undefined
          let favicon = result?.favicon || undefined

          return {
            nodeType: "embedded-entry-block",
            data: {
              target: {
                sys: {
                  type: "Link",
                  linkType: "Asset",
                  contentType: {
                    sys: {
                      id: "post",
                    },
                  },
                },
                fields: {
                  file: {
                    contentType: contentType,
                    url: url,
                  },
                  title: title,
                  subtitle: description,
                  slug: undefined,
                  siteName,
                  favicon,
                  url: url,
                  image: {
                    fields: {
                      file: {
                        url: image,
                      },
                    },
                    title: title,
                  },
                  node,
                  result,
                },
              },
            },
            content: [],
          } as Node
        }

        let desciption = ""

        try {
          desciption = contentType.split("/")[1] || ""
          desciption = desciption.split(";")[0] || ""
        } catch (e) {
          desciption = "Download"
        }

        // for image and videos only
        if (contentType.includes("image") || contentType.includes("video")) {
          return {
            nodeType: "embedded-asset-block",
            data: {
              target: {
                sys: {
                  type: "Link",
                  linkType: "Asset",
                },
                fields: {
                  file: {
                    contentType: contentType,
                    url: url,
                  },
                  title: node.desciption,
                  description: node.alt,
                  node,
                },
              },
            },
            content: [],
          } as Node
        } else {
          return {
            nodeType: "embedded-asset-block",
            data: {
              target: {
                sys: {
                  type: "Link",
                  linkType: "Asset",
                },
                fields: {
                  file: {
                    contentType: contentType,
                    url: url,
                  },
                  title: node.alt || "Download",
                  description: node.alt
                    ? node.alt.toLowerCase() !== "download"
                      ? "Download"
                      : desciption
                    : desciption,
                  node,
                },
              },
            },
            content: [],
          } as Node
        }
      }
      return []
    }

    if (post.fields.markdownBody && post.fields.markdownBody.trim() !== "") {
      body = await richTextFromMarkdown(post.fields.markdownBody, fallback)
    }

    let output = {
      title: post.fields.title,
      subtitle: post.fields.subtitle,
      slug: post.fields.slug,
      markdownBody: post.fields.markdownBody,
      image: image
        ? ({
            title: image.fields.title,
            description: image.fields.description,
            contentType: image.fields.file.contentType,
            fileName: image.fields.file.fileName,
            url: image.fields.file.url,
          } as PostMedia)
        : undefined,
      tags: post.metadata.tags.map((tag) => _.startCase(tag.sys.id)),
      createdAt: post.fields.date || post.sys.createdAt,
      updatedPostPublishDate: post.fields.updatedPostPublishDate,
      updatedAt: post.sys.updatedAt,
      body: body || undefined,
      author: post.fields.author ? this.mapMember(post.fields.author) : undefined,
      sections: post.fields.body ? this.createHeadingTree(post.fields.body) : undefined,
    }

    return output
  }

  mapMember(member: any): Member {
    const image = member.fields.image || {}

    return {
      name: member.fields.name,
      slug: member.fields.slug,
      title: member.fields.title,
      image: {
        title: image.fields.title,
        description: image.fields.description,
        contentType: image.fields.file.contentType,
        fileName: image.fields.file.fileName,
        url: image.fields.file.url,
      } as PostMedia,
      email: member.fields.email,
      twitter: member.fields.twitter,
      linkedin: member.fields.linkedin,
    }
  }

  mapPage(page: any): Page {
    const image = page.fields.image || false

    return {
      title: page.fields.title,
      subtitle: page.fields.subtitle,
      slug: page.fields.slug,
      image: image
        ? ({
            title: image.fields.title,
            description: image.fields.description,
            contentType: image.fields.file.contentType,
            fileName: image.fields.file.fileName,
            url: image.fields.file.url,
          } as PostMedia)
        : undefined,
      body: page.fields.body || undefined,
      section: page.fields.section,
    }
  }

  createHeadingTree(document: any): any {
    const headings: Section[] = []
    const content = document.content

    content.forEach((node) => {
      if (node.nodeType.startsWith("heading-")) {
        headings.push({
          level: parseInt(node.nodeType.replace("heading-", "")),
          name: node.content[0].value,
        })
      }
    })

    return headings
  }
}

export type PostMedia = {
  title: string
  description: string
  contentType: string
  fileName: string
  url: string
}

export type Section = {
  level: number
  name: string
}

export type Post = {
  title: string
  subtitle: string
  slug: string
  image?: PostMedia
  tags: string[]
  createdAt: Moment
  updatedPostPublishDate?: Moment
  updatedAt: Moment
  body: any | undefined
  markdownBody: string | undefined
  author?: Member
  sections?: Section[]
}

export type Member = {
  name: string
  slug: string
  title: string
  image: PostMedia
  email: string
  twitter: string
  linkedin: string
}

export type Page = {
  title: string
  subtitle: string
  slug: string
  image?: PostMedia
  body: any | undefined
  section: string
}

const contentful = new Contentful()

export default contentful
